import React from 'react';
import {
  Alert,
  Box,
  Button,
  TextField,
  Autocomplete,
  Typography,
  Grid,
} from '@mui/material';
import { CloudAccountFormProps } from './types';

export const CloudAccountForm: React.FC<CloudAccountFormProps> = ({
  accountValue,
  setAccountValue,
  providers,
  users,
  canManageOwner = true,
  canEditAccount = true,
  alerts,
  onCreateAccount,
  onDeleteAccount,
}) => {
  const {
    alertProvider,
    alertTenantName,
    alertAccount,
    alertSecret,
    alertTenantId,
    alertOwner,
  } = alerts;

  const selectedProvider =
    providers.find((row) => row.id === accountValue.provider_id) ?? null;
  const selectedProviderName =
    selectedProvider?.name ?? accountValue.provider_name ?? '';
  const isAzure = selectedProviderName === 'Azure';
  const isAWS = selectedProviderName === 'AWS';

  const handleProviderChange = (provider: any) => {
    if (provider) {
      setAccountValue({
        ...accountValue,
        provider_id: provider.id,
        account: '',
        secret: '',
        tenant_id: '',
        master_account: '',
        report_name: '',
        report_prefix: '',
        bucket: '',
        region: '',
      });
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 3,
      maxHeight: '70vh',
      overflowY: 'auto',
      pr: 1, // Add right padding for scrollbar
    }}>
      {!accountValue.new && (
        <Typography variant="h6" component="h3" sx={{ mb: 2, flexShrink: 0 }}>
          Cloud Account Settings
        </Typography>
      )}

      {/* Common fields for both providers - shown first */}
      <TextField
        id="tenant-name"
        label="Tenant Name"
        variant="outlined"
        fullWidth
        disabled={!canEditAccount}
        value={accountValue.name ?? ''}
        onChange={(e) =>
          setAccountValue({ ...accountValue, name: e.target.value })
        }
        inputProps={{ 'data-testid': 'cloud-account-creation-tenant-name' }}
      />
      {alertTenantName && (
        <Alert severity="error">Tenant Name is required</Alert>
      )}
      {canEditAccount ? (
        <Autocomplete
          disablePortal
          id="combo-box-owners"
          options={users}
          disabled={!canManageOwner || !canEditAccount}
          value={users.find((row) => row.id === accountValue.owner_id) ?? null}
          getOptionLabel={(option) => option.username}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Cloud Account Owner"
              variant="outlined"
              inputProps={{
                ...params.inputProps,
                'data-testid': 'cloud-account-creation-owner',
              }}
            />
          )}
          onChange={(_, value) => {
            if (canManageOwner && value) {
              setAccountValue({ ...accountValue, owner_id: value.id });
            }
          }}
        />
      ) : (
        <TextField
          label="Cloud Account Owner"
          variant="outlined"
          fullWidth
          disabled
          value={accountValue.owner_name ?? String(accountValue.owner_id ?? '')}
        />
      )}
      {alertOwner && (
        <Alert severity="error">
          You did not assign Cloud Account Owner to this cloud account. If the
          user account is not available from the list, ensure that account is
          created for the user and associated with the TECH profile.
        </Alert>
      )}
      {canEditAccount ? (
        <Autocomplete
          disablePortal
          id="combo-box-providers"
          options={providers}
          disabled={!canEditAccount}
          value={providers.find((row) => row.id === accountValue.provider_id) ?? null}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Cloud Provider"
              variant="outlined"
              inputProps={{
                ...params.inputProps,
                'data-testid': 'cloud-account-creation-provider',
              }}
            />
          )}
          onChange={(_, value) => handleProviderChange(value)}
        />
      ) : (
        <TextField
          label="Cloud Provider"
          variant="outlined"
          fullWidth
          disabled
          value={selectedProviderName}
        />
      )}
      {alertProvider && (
        <Alert severity="error">Cloud Provider is required</Alert>
      )}
      {/* Provider-specific fields - shown after common fields */}
      {isAzure && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 3, color: 'text.secondary' }}>
            Azure-Specific Configuration
          </Typography>
          
          <TextField
            id="account-id"
            label="Client ID/Application ID"
            variant="outlined"
            fullWidth
            disabled={!canEditAccount}
            value={accountValue.account ?? ''}
            onChange={(e) =>
              setAccountValue({ ...accountValue, account: e.target.value })
            }
            sx={{ mb: 2 }}
            inputProps={{ 'data-testid': 'cloud-account-creation-client-id' }}
          />
          {alertAccount && (
            <Alert severity="error">Client ID/Application ID is required</Alert>
          )}

          <TextField
            id="account-secret"
            label="Account Secret"
            type="password"
            variant="outlined"
            fullWidth
            disabled={!canEditAccount}
            value={accountValue.secret ?? ''}
            onChange={(e) =>
              setAccountValue({ ...accountValue, secret: e.target.value })
            }
            sx={{ mb: 2 }}
            inputProps={{ 'data-testid': 'cloud-account-creation-account-secret' }}
          />
          {alertSecret && (
            <Alert severity="error">Account Secret is required</Alert>
          )}

          <TextField
            id="tenant-id"
            label="Tenant ID"
            variant="outlined"
            fullWidth
            disabled={!canEditAccount}
            value={accountValue.tenant_id ?? ''}
            onChange={(e) =>
              setAccountValue({
                ...accountValue,
                tenant_id: e.target.value,
              })
            }
            inputProps={{ 'data-testid': 'cloud-account-creation-tenant-id' }}
          />
          {alertTenantId && (
            <Alert severity="error">Tenant ID is required</Alert>
          )}
        </Box>
      )}

      {isAWS && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 3, color: 'text.secondary' }}>
            AWS-Specific Configuration
          </Typography>
          
          <TextField
            id="account-id"
            label="Access Key ID"
            variant="outlined"
            fullWidth
            disabled={!canEditAccount}
            value={accountValue.account ?? ''}
            onChange={(e) =>
              setAccountValue({ ...accountValue, account: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          {alertAccount && (
            <Alert severity="error">Access Key ID is required</Alert>
          )}

          <TextField
            id="account-secret"
            label="Secret Access Key"
            type="password"
            variant="outlined"
            fullWidth
            disabled={!canEditAccount}
            value={accountValue.secret ?? ''}
            onChange={(e) =>
              setAccountValue({ ...accountValue, secret: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          {alertSecret && (
            <Alert severity="error">Secret Access Key is required</Alert>
          )}

          <TextField
            id="master-account"
            label="Master Account"
            variant="outlined"
            fullWidth
            disabled={!canEditAccount}
            value={accountValue.master_account ?? ''}
            onChange={(e) =>
              setAccountValue({
                ...accountValue,
                master_account: e.target.value,
              })
            }
            sx={{ mb: 2 }}
          />

          <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
            <Grid item xs={6}>
              <TextField
                id="report-name"
                label="Report Name"
                variant="outlined"
                fullWidth
                disabled={!canEditAccount}
                value={accountValue.report_name ?? ''}
                onChange={(e) =>
                  setAccountValue({
                    ...accountValue,
                    report_name: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="report-prefix"
                label="Report Prefix"
                variant="outlined"
                fullWidth
                disabled={!canEditAccount}
                value={accountValue.report_prefix ?? ''}
                onChange={(e) =>
                  setAccountValue({
                    ...accountValue,
                    report_prefix: e.target.value,
                  })
                }
              />
            </Grid>
          </Grid>

          <TextField
            id="bucket"
            label="S3 Bucket"
            variant="outlined"
            fullWidth
            disabled={!canEditAccount}
            value={accountValue.bucket ?? ''}
            onChange={(e) =>
              setAccountValue({
                ...accountValue,
                bucket: e.target.value,
              })
            }
            sx={{ mb: 2 }}
          />

          <Autocomplete
            disablePortal
            id="combo-box-region"
            options={[
              'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
              'eu-west-1', 'eu-west-2', 'eu-west-3',
              'eu-central-1', 'eu-north-1',
              'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1', 'ap-northeast-2',
              'sa-east-1', 'ca-central-1'
            ]}
            value={accountValue.region ?? ''}
            renderInput={(params) => (
              <TextField
                {...params}
                label="AWS Region"
                variant="outlined"
                disabled={!canEditAccount}
              />
            )}
            onChange={(_, value) => {
              setAccountValue({
                ...accountValue,
                region: value || '',
              });
            }}
            freeSolo
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {canEditAccount && (
          <>
            <Button
              variant="contained"
              size='small'
              onClick={onCreateAccount}
              color="primary"
              disabled={!accountValue.provider_id || !accountValue.name || !accountValue.owner_id}
              fullWidth
              data-testid={accountValue.new ? 'cloud-account-creation-submit' : 'cloud-account-edit-submit'}>
              {accountValue.new ? 'Create Account' : 'Update Account'}
            </Button>
            {!accountValue.new && (
              <Button
                variant="contained"
                size='small'
                onClick={onDeleteAccount}
                color="error"
                fullWidth
                data-testid="cloud-account-delete">
                Delete Account
              </Button>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
